"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildTradeTx = exports.performIbcTransfer = exports.getRateInfo = exports.pollForAtomChannelBalance = exports.getAtomChannelBalance = exports.pollForComplete = exports.symbolDenomMapping = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const chain_adapters_1 = require("@shapeshiftoss/chain-adapters");
const axios_1 = __importDefault(require("axios"));
const lodash_1 = require("lodash");
const api_1 = require("../../../api");
const bignumber_1 = require("../../utils/bignumber");
const constants_1 = require("./constants");
exports.symbolDenomMapping = {
    OSMO: 'uosmo',
    ATOM: 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2',
    USDC: 'ibc/D189335C6E4A68B513C10AB227BF1C1D38C746766278BA3EEB4FB14124F1D858',
};
const txStatus = async (txid, baseUrl) => {
    var _a, _b, _c;
    try {
        const txResponse = await axios_1.default.get(`${baseUrl}/txs/${txid}`);
        if (!((_a = txResponse === null || txResponse === void 0 ? void 0 : txResponse.data) === null || _a === void 0 ? void 0 : _a.codespace) && !!((_b = txResponse === null || txResponse === void 0 ? void 0 : txResponse.data) === null || _b === void 0 ? void 0 : _b.gas_used))
            return 'success';
        if ((_c = txResponse === null || txResponse === void 0 ? void 0 : txResponse.data) === null || _c === void 0 ? void 0 : _c.codespace)
            return 'failed';
    }
    catch (e) {
        console.warn('Retrying to retrieve status');
    }
    return 'not found';
};
// TODO: leverage chain-adapters websockets
const pollForComplete = async (txid, baseUrl) => {
    return new Promise((resolve, reject) => {
        const timeout = 300000; // 5 mins
        const startTime = Date.now();
        const interval = 5000; // 5 seconds
        const poll = async function () {
            const status = await txStatus(txid, baseUrl);
            if (status === 'success') {
                resolve(status);
            }
            else if (Date.now() - startTime > timeout) {
                reject(new api_1.SwapError(`Couldnt find tx ${txid}`, {
                    code: api_1.SwapErrorTypes.RESPONSE_ERROR,
                }));
            }
            else {
                setTimeout(poll, interval);
            }
        };
        poll();
    });
};
exports.pollForComplete = pollForComplete;
const getAtomChannelBalance = async (address, osmoUrl) => {
    const osmoResponseBalance = await (async () => {
        try {
            return axios_1.default.get(`${osmoUrl}/bank/balances/${address}`);
        }
        catch (e) {
            throw new api_1.SwapError('failed to get balance', {
                code: api_1.SwapErrorTypes.RESPONSE_ERROR,
            });
        }
    })();
    let toAtomChannelBalance = 0;
    try {
        const { amount } = (0, lodash_1.find)(osmoResponseBalance.data.result, (b) => b.denom === exports.symbolDenomMapping.ATOM);
        toAtomChannelBalance = Number(amount);
    }
    catch (e) {
        console.warn('Retrying to get ibc balance');
    }
    return toAtomChannelBalance;
};
exports.getAtomChannelBalance = getAtomChannelBalance;
const pollForAtomChannelBalance = async (address, osmoUrl) => {
    return new Promise((resolve, reject) => {
        const timeout = 300000; // 5 mins
        const startTime = Date.now();
        const interval = 5000; // 5 seconds
        const poll = async function () {
            const balance = await (0, exports.getAtomChannelBalance)(address, osmoUrl);
            if (balance > 0) {
                resolve(balance.toString());
            }
            else if (Date.now() - startTime > timeout) {
                reject(new api_1.SwapError(`Couldnt find channel balance for ${address}`, {
                    code: api_1.SwapErrorTypes.RESPONSE_ERROR,
                }));
            }
            else {
                setTimeout(poll, interval);
            }
        };
        poll();
    });
};
exports.pollForAtomChannelBalance = pollForAtomChannelBalance;
const findPool = async (sellAssetSymbol, buyAssetSymbol, osmoUrl) => {
    const sellAssetDenom = exports.symbolDenomMapping[sellAssetSymbol];
    const buyAssetDenom = exports.symbolDenomMapping[buyAssetSymbol];
    const poolsUrl = osmoUrl + '/osmosis/gamm/v1beta1/pools?pagination.limit=1000';
    const poolsResponse = await (async () => {
        try {
            return axios_1.default.get(poolsUrl);
        }
        catch (e) {
            throw new api_1.SwapError('failed to get pool', {
                code: api_1.SwapErrorTypes.POOL_NOT_FOUND,
            });
        }
    })();
    const foundPool = (0, lodash_1.find)(poolsResponse.data.pools, (pool) => {
        const token0Denom = pool.pool_assets[0].token.denom;
        const token1Denom = pool.pool_assets[1].token.denom;
        return ((token0Denom === sellAssetDenom && token1Denom === buyAssetDenom) ||
            (token0Denom === buyAssetDenom && token1Denom === sellAssetDenom));
    });
    if (!foundPool)
        throw new api_1.SwapError('could not find pool', {
            code: api_1.SwapErrorTypes.POOL_NOT_FOUND,
        });
    const { sellAssetIndex, buyAssetIndex } = (() => {
        if (foundPool.pool_assets[0].token.denom === sellAssetDenom) {
            return { sellAssetIndex: 0, buyAssetIndex: 1 };
        }
        else {
            return { sellAssetIndex: 1, buyAssetIndex: 0 };
        }
    })();
    return { pool: foundPool, sellAssetIndex, buyAssetIndex };
};
const getInfoFromPool = (sellAmount, pool, sellAssetIndex, buyAssetIndex) => {
    const constantProduct = (0, bignumber_1.bnOrZero)(pool.pool_assets[0].token.amount).times(pool.pool_assets[1].token.amount);
    const sellAssetInitialPoolSize = (0, bignumber_1.bnOrZero)(pool.pool_assets[sellAssetIndex].token.amount);
    const buyAssetInitialPoolSize = (0, bignumber_1.bnOrZero)(pool.pool_assets[buyAssetIndex].token.amount);
    const initialMarketPrice = sellAssetInitialPoolSize.dividedBy(buyAssetInitialPoolSize);
    const sellAssetFinalPoolSize = sellAssetInitialPoolSize.plus(sellAmount);
    const buyAssetFinalPoolSize = constantProduct.dividedBy(sellAssetFinalPoolSize);
    const finalMarketPrice = sellAssetFinalPoolSize.dividedBy(buyAssetFinalPoolSize);
    const buyAmountCryptoBaseUnit = buyAssetInitialPoolSize.minus(buyAssetFinalPoolSize).toString();
    const rate = (0, bignumber_1.bnOrZero)(buyAmountCryptoBaseUnit).dividedBy(sellAmount).toString();
    const priceImpact = (0, bignumber_1.bn)(1).minus(initialMarketPrice.dividedBy(finalMarketPrice)).abs().toString();
    const tradeFeeBase = (0, bignumber_1.bnOrZero)(buyAmountCryptoBaseUnit).times((0, bignumber_1.bnOrZero)(pool.pool_params.swap_fee));
    const buyAssetTradeFeeUsd = tradeFeeBase
        .dividedBy((0, bignumber_1.bn)(10).exponentiatedBy(constants_1.OSMOSIS_PRECISION))
        .toString();
    return {
        rate,
        priceImpact,
        buyAssetTradeFeeUsd,
        buyAmountCryptoBaseUnit,
    };
};
const getRateInfo = async (sellAsset, buyAsset, sellAmount, osmoUrl) => {
    const { pool, sellAssetIndex, buyAssetIndex } = await findPool(sellAsset, buyAsset, osmoUrl);
    return getInfoFromPool(sellAmount, pool, sellAssetIndex, buyAssetIndex);
};
exports.getRateInfo = getRateInfo;
// TODO: move to chain adapters
const performIbcTransfer = async (input, adapter, wallet, blockBaseUrl, denom, sourceChannel, feeAmount, accountNumber, sequence, gas, feeDenom) => {
    const { sender, receiver, amount } = input;
    const responseLatestBlock = await (async () => {
        try {
            return axios_1.default.get(`${blockBaseUrl}/blocks/latest`);
        }
        catch (e) {
            throw new api_1.SwapError('failed to get latest block', {
                code: api_1.SwapErrorTypes.RESPONSE_ERROR,
            });
        }
    })();
    const latestBlock = responseLatestBlock.data.block.header.height;
    const tx = {
        memo: '',
        fee: {
            amount: [
                {
                    amount: feeAmount.toString(),
                    denom: feeDenom,
                },
            ],
            gas,
        },
        signatures: [],
        msg: [
            {
                type: 'cosmos-sdk/MsgTransfer',
                value: {
                    source_port: 'transfer',
                    source_channel: sourceChannel,
                    token: {
                        denom,
                        amount,
                    },
                    sender,
                    receiver,
                    timeout_height: {
                        revision_number: '4',
                        revision_height: String(Number(latestBlock) + 100),
                    },
                },
            },
        ],
    };
    const signed = await adapter.signTransaction({
        txToSign: {
            tx,
            addressNList: (0, chain_adapters_1.toAddressNList)(adapter.buildBIP44Params({ accountNumber: 0 })),
            chain_id: (0, caip_1.fromChainId)(adapter.getChainId()).chainReference,
            account_number: accountNumber,
            sequence,
        },
        wallet,
    });
    const tradeId = await adapter.broadcastTransaction(signed);
    return {
        tradeId,
    };
};
exports.performIbcTransfer = performIbcTransfer;
// TODO: move to chain adapters
const buildTradeTx = async ({ osmoAddress, adapter, buyAssetDenom, sellAssetDenom, sellAmount, gas, wallet, }) => {
    const responseAccount = await adapter.getAccount(osmoAddress);
    const accountNumber = responseAccount.chainSpecific.accountNumber || '0';
    const sequence = responseAccount.chainSpecific.sequence || '0';
    const tx = {
        memo: '',
        fee: {
            amount: [
                {
                    amount: '0',
                    denom: 'uosmo',
                },
            ],
            gas,
        },
        signatures: [],
        msg: [
            {
                type: 'osmosis/gamm/swap-exact-amount-in',
                value: {
                    sender: osmoAddress,
                    routes: [
                        {
                            poolId: '1',
                            tokenOutDenom: buyAssetDenom,
                        },
                    ],
                    tokenIn: {
                        denom: sellAssetDenom,
                        amount: sellAmount,
                    },
                    tokenOutMinAmount: '1', // slippage tolerance
                },
            },
        ],
    };
    return {
        txToSign: {
            tx,
            addressNList: (0, chain_adapters_1.toAddressNList)(adapter.buildBIP44Params({ accountNumber: 0 })),
            chain_id: caip_1.CHAIN_REFERENCE.OsmosisMainnet,
            account_number: accountNumber,
            sequence,
        },
        wallet,
    };
};
exports.buildTradeTx = buildTradeTx;
